import { isBiome, isLight, isShape } from '../world/biome';
import { LEVELS, levelById } from '../sim/campaign/levels';
import { isWeekId, weeklyLevel } from '../sim/campaign/weekly';
import type { GameConfig } from './gameConfig';

/**
 * Optional URL overrides for map and tile dimensions, mainly for testing and
 * level tuning:  ?map=96x64&tile=96x48&seed=7&players=3&speed=5&resources=wood:10,scrap:0,food:40&units=4&enemies=6&zombies=40&waves=0
 *
 * Every value is validated; invalid values are ignored (with a warning) so a
 * typo can never produce a broken world.
 */
export const LIMITS = {
  mapMin: 8,
  mapMax: 256,
  tileMin: 16,
  tileMax: 256,
} as const;

export interface RuntimeConfigResult {
  readonly config: GameConfig;
  readonly warnings: readonly string[];
}

export function applyRuntimeOverrides(base: GameConfig, search: string): RuntimeConfigResult {
  const params = new URLSearchParams(search);
  const warnings: string[] = [];
  let map = base.map;
  let tile = base.tile;

  const mapParam = params.get('map');
  if (mapParam !== null) {
    const dims = parseDims(mapParam);
    if (!dims) warnings.push(`Ignored map="${mapParam}": expected WIDTHxHEIGHT, e.g. 96x64`);
    else if (!inRange(dims.w, LIMITS.mapMin, LIMITS.mapMax) || !inRange(dims.h, LIMITS.mapMin, LIMITS.mapMax)) {
      warnings.push(`Ignored map="${mapParam}": each side must be ${LIMITS.mapMin}–${LIMITS.mapMax} tiles`);
    } else {
      map = { ...map, width: dims.w, height: dims.h };
    }
  }

  const tileParam = params.get('tile');
  if (tileParam !== null) {
    const dims = parseDims(tileParam);
    if (!dims) warnings.push(`Ignored tile="${tileParam}": expected WIDTHxHEIGHT, e.g. 64x32`);
    else if (!inRange(dims.w, LIMITS.tileMin, LIMITS.tileMax) || !inRange(dims.h, LIMITS.tileMin / 2, LIMITS.tileMax)) {
      warnings.push(`Ignored tile="${tileParam}": width ${LIMITS.tileMin}–${LIMITS.tileMax} px, height ${LIMITS.tileMin / 2}–${LIMITS.tileMax} px`);
    } else if (dims.w % 2 !== 0 || dims.h % 2 !== 0) {
      warnings.push(`Ignored tile="${tileParam}": width and height must be even so tile corners land on whole pixels`);
    } else if (dims.h > dims.w) {
      warnings.push(`Ignored tile="${tileParam}": height may not exceed width`);
    } else {
      tile = { width: dims.w, height: dims.h };
    }
  }

  const seedParam = params.get('seed');
  if (seedParam !== null) {
    const seed = Number(seedParam);
    if (!Number.isInteger(seed) || seed < 0 || seed > 0xffffffff) warnings.push(`Ignored seed="${seedParam}": expected a whole number ≥ 0`);
    else map = { ...map, seed };
  }

  let colony = base.colony;
  const playersParam = params.get('players');
  if (playersParam !== null) {
    const n = Number(playersParam);
    if (!Number.isInteger(n) || n < 1 || n > 4) warnings.push(`Ignored players="${playersParam}": expected 1–4`);
    else colony = { ...colony, players: n };
  }

  let simulation = base.simulation;
  const speedParam = params.get('speed');
  if (speedParam !== null) {
    const v = Number(speedParam);
    if (!Number.isFinite(v) || v < 0.25 || v > 20) warnings.push(`Ignored speed="${speedParam}": expected 0.25–20`);
    else simulation = { ...simulation, speed: v, maxTicksPerFrame: Math.max(simulation.maxTicksPerFrame, Math.ceil(5 * v)) };
  }

  const resParam = params.get('resources');
  if (resParam !== null) {
    const parsed = parseResources(resParam);
    if (!parsed) warnings.push(`Ignored resources="${resParam}": expected e.g. wood:100,scrap:20,food:50 (whole numbers 0–100000)`);
    else colony = { ...colony, startingResources: parsed };
  }

  const survParam = params.get('survivors');
  if (survParam !== null) {
    const n = Number(survParam);
    if (!Number.isInteger(n) || n < 0 || n > 200) warnings.push(`Ignored survivors="${survParam}": expected 0–200`);
    else colony = { ...colony, startingSurvivors: n };
  }

  const unitsParam = params.get('units');
  if (unitsParam !== null) {
    const n = Number(unitsParam);
    const mix = parseUnitMix(unitsParam);
    if (Number.isInteger(n) && n >= 0 && n <= 500) colony = { ...colony, startingUnits: n };
    else if (mix) colony = { ...colony, startingUnits: mix };
    else warnings.push(`Ignored units="${unitsParam}": expected 0–500, or a mix like ranger:3,heavy:2,engineer:1 (total ≤ 500)`);
  }

  const enemiesParam = params.get('enemies');
  if (enemiesParam !== null) {
    const n = Number(enemiesParam);
    if (!Number.isInteger(n) || n < 0 || n > 200) warnings.push(`Ignored enemies="${enemiesParam}": expected 0–200`);
    // An enemy squad needs a second player to own it.
    else colony = { ...colony, startingEnemies: n, players: n > 0 ? Math.max(2, colony.players) : colony.players };
  }

  const zParam = params.get('zombies');
  if (zParam !== null) {
    const n = Number(zParam);
    if (!Number.isInteger(n) || n < 0 || n > 500) warnings.push(`Ignored zombies="${zParam}": expected 0–500`);
    else colony = { ...colony, startingInfected: n };
  }

  const wavesParam = params.get('waves');
  if (wavesParam !== null) {
    if (wavesParam !== '0' && wavesParam !== '1') warnings.push(`Ignored waves="${wavesParam}": expected 0 or 1`);
    else colony = { ...colony, waves: wavesParam === '1' };
  }

  const modeParam = params.get('mode');
  if (modeParam !== null) {
    if (!['sandbox', 'classic', 'last_colony', 'raid', 'duel'].includes(modeParam)) warnings.push(`Ignored mode="${modeParam}": expected sandbox, classic, last_colony, raid or duel`);
    else colony = { ...colony, mode: modeParam as 'sandbox' | 'classic' | 'last_colony' | 'raid' | 'duel' };
  }

  const levelParam = params.get('level');
  if (levelParam !== null) {
    const n = Number(levelParam);
    const l = Number.isInteger(n) ? levelById(n) : undefined;
    if (!l) warnings.push(`Ignored level="${levelParam}": expected 0–${LEVELS.length}`);
    else {
      colony = { ...colony, level: n, players: 1 };
      // The level's own map, unless the URL sets one explicitly (tests).
      if (params.get('seed') === null) map = { ...map, seed: l.map.seed };
      if (params.get('map') === null) map = { ...map, width: l.map.width, height: l.map.height };
      map = { ...map, biome: l.map.biome, light: l.map.light, shape: l.map.shape };
    }
  }

  const weeklyParam = params.get('weekly');
  if (weeklyParam !== null) {
    if (!isWeekId(weeklyParam)) warnings.push(`Ignored weekly="${weeklyParam}": expected a week like 2026-W45`);
    else {
      const l = weeklyLevel(weeklyParam);
      colony = { ...colony, weekly: weeklyParam, players: 1 };
      if (params.get('seed') === null) map = { ...map, seed: l.map.seed };
      if (params.get('map') === null) map = { ...map, width: l.map.width, height: l.map.height };
      map = { ...map, biome: l.map.biome, light: l.map.light, shape: l.map.shape };
    }
  }

  // Look / time / layout overrides (D132), e.g. ?biome=tundra&light=night&shape=river.
  const biomeParam = params.get('biome');
  if (biomeParam !== null) {
    if (!isBiome(biomeParam)) warnings.push(`Ignored biome="${biomeParam}"`);
    else map = { ...map, biome: biomeParam };
  }
  const lightParam = params.get('light');
  if (lightParam !== null) {
    if (!isLight(lightParam)) warnings.push(`Ignored light="${lightParam}"`);
    else map = { ...map, light: lightParam };
  }
  const shapeParam = params.get('shape');
  if (shapeParam !== null) {
    if (!isShape(shapeParam)) warnings.push(`Ignored shape="${shapeParam}"`);
    else map = { ...map, shape: shapeParam };
  }

  // Keep the start clearing proportionate on small maps.
  const maxRadius = Math.max(2, Math.floor(Math.min(map.width, map.height) / 6));
  if (map.startClearRadius > maxRadius) map = { ...map, startClearRadius: maxRadius };

  return { config: { ...base, map, tile, colony, simulation }, warnings };
}

/** "ranger:3,engineer:2" → { ranger: 3, engineer: 2 } (total ≤ 500), or null if malformed. */
function parseUnitMix(v: string): Partial<Record<'ranger' | 'heavy' | 'engineer', number>> | null {
  const out: Partial<Record<'ranger' | 'heavy' | 'engineer', number>> = {};
  let total = 0;
  for (const part of v.split(',')) {
    const m = /^\s*(ranger|heavy|engineer)\s*:\s*(\d{1,3})\s*$/.exec(part);
    if (!m) return null;
    const n = Number(m[2]);
    out[m[1] as 'ranger' | 'heavy' | 'engineer'] = n;
    total += n;
  }
  return total <= 500 && Object.keys(out).length ? out : null;
}

/** "wood:100,scrap:20" → { wood: 100, scrap: 20 }. Unlisted resources keep their defaults. Null if malformed. */
function parseResources(v: string): Partial<Record<'wood' | 'scrap' | 'food', number>> | null {
  const out: Partial<Record<'wood' | 'scrap' | 'food', number>> = {};
  for (const part of v.split(',')) {
    const m = /^\s*(wood|scrap|food)\s*:\s*(\d{1,6})\s*$/.exec(part);
    if (!m) return null;
    const n = Number(m[2]);
    if (n > 100000) return null;
    out[m[1] as 'wood' | 'scrap' | 'food'] = n;
  }
  return Object.keys(out).length ? out : null;
}

function parseDims(v: string): { w: number; h: number } | null {
  const m = /^\s*(\d{1,4})\s*[xX×]\s*(\d{1,4})\s*$/.exec(v);
  if (!m) return null;
  return { w: Number(m[1]), h: Number(m[2]) };
}

function inRange(v: number, min: number, max: number): boolean {
  return v >= min && v <= max;
}
